<template>
  <v-card elevation="2" style="height: 300px; display: flex; flex-direction: column;">
    <v-card-title class="subtitle-2 font-weight-black black white--text pa-2" style="height: 45px; flex-shrink: 0;">
      <v-icon class="mr-2" color="primary">{{ icon }}</v-icon>
      {{ title }}
      <slot name="title-actions"></slot>
    </v-card-title>

    <div style="flex: 1 1 auto; overflow: hidden; display: flex; flex-direction: column;">
      <!-- Loading state -->
      <v-card-text v-if="loading" class="d-flex flex-column align-center justify-center" style="flex: 1 1 auto;">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-4 grey--text">{{ displayLoadingText }}</div>
      </v-card-text>

      <!-- Empty state -->
      <v-card-text v-else-if="items.length === 0" class="d-flex flex-column align-center justify-center" style="flex: 1 1 auto;">
        <v-icon size="64" color="grey lighten-1">{{ emptyIcon }}</v-icon>
        <div class="text-body-1 grey--text text--darken-1 mt-4">{{ displayEmptyTitle }}</div>
        <div class="text-body-2 grey--text mt-2">{{ displayEmptyMessage }}</div>
      </v-card-text>

      <!-- Data display -->
      <v-list v-else dense style="flex: 1 1 auto; overflow-y: auto;">
        <template v-for="item in items">
          <slot name="item" :item="item"></slot>
        </template>
      </v-list>

      <!-- Pagination controls -->
      <v-card-actions v-if="showPagination" class="justify-center pa-2" style="flex-shrink: 0;">
        <v-btn
          small
          icon
          :disabled="isPrevDisabled"
          @click="goToPrevPage"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="mx-3 text-caption grey--text">{{ $t('components.MetricsListBox.page') }} {{ currentPage }}</span>
        <v-btn
          small
          icon
          :disabled="isNextDisabled"
          @click="goToNextPage"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-card-actions>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'MetricsListBox',
  computed: {
    isPrevDisabled () {
      return this.currentPage <= 1
    },
    isNextDisabled () {
      return !this.hasMore
    },
    showPagination () {
      return !this.loading && this.items.length > 0
    },
    displayLoadingText () {
      return this.loadingText || this.$t('components.MetricsListBox.loading')
    },
    displayEmptyTitle () {
      return this.emptyTitle || this.$t('components.MetricsListBox.noItemsFound')
    },
    displayEmptyMessage () {
      return this.emptyMessage || this.$t('components.MetricsListBox.itemsWillAppear')
    }
  },
  methods: {
    goToPrevPage () {
      if (!this.isPrevDisabled) {
        this.$emit('page-change', this.currentPage - 1)
      }
    },
    goToNextPage () {
      if (!this.isNextDisabled) {
        this.$emit('page-change', this.currentPage + 1)
      }
    }
  },
  props: {
    title: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      default: function () {
        return 'mdi-folder-open-outline'
      },
      required: false
    },
    items: {
      type: Array,
      default: function () {
        return []
      },
      required: false
    },
    loading: {
      type: Boolean,
      default: function () {
        return false
      },
      required: false
    },
    loadingText: {
      type: String,
      default: null,
      required: false
    },
    emptyIcon: {
      type: String,
      default: function () {
        return 'mdi-folder-open-outline'
      },
      required: false
    },
    emptyTitle: {
      type: String,
      default: null,
      required: false
    },
    emptyMessage: {
      type: String,
      default: null,
      required: false
    },
    pageSize: {
      type: Number,
      default: function () {
        return 4
      },
      required: false
    },
    currentPage: {
      type: Number,
      default: function () {
        return 1
      },
      required: false
    },
    hasMore: {
      type: Boolean,
      default: function () {
        return false
      },
      required: false
    }
  }
}
</script>

<style scoped>
.compact-list-item {
  padding-top: 1px !important;
  padding-bottom: 1px !important;
  min-height: auto !important;
}

::v-deep .compact-list-item .v-list-item__content {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

::v-deep .compact-list-item .v-list-item__title {
  line-height: 1.2 !important;
  margin-bottom: 0 !important;
  font-size: 0.875rem !important;
}

::v-deep .compact-list-item .v-list-item__subtitle {
  margin-bottom: 0 !important;
  margin-top: 0 !important;
  line-height: 1.1 !important;
  padding-top: 0 !important;
}
</style>
